import {
  Flex,
  Icon,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import {
  ConfidenceLevel,
  KeyTermType,
  KeyTermUnit,
  MarketDataSourceType,
  UserObject,
} from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { CircleAlertIcon } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';
import { CurrencyInput } from '~/common/components/Inputs/CurrencyInput';
import { SignalStrength } from '~/common/icons/SignalIcon';
import { formatToCurrency } from '~/services/document/utils/number';
import { formatAgentRecommendation } from './common.utils';
import { COLLAPSED_ROW_HEIGHT, EXPANDED_ROW_HEIGHT } from './constants';
import {
  BaseKeyTermLogicProps,
  useKeyTermOverrideLogic,
} from './hooks/useKeyTermOverrideLogic';
import { KeyTermsRangeChart } from './KeyTermRangeChart/KeyTermsRangeChart';
import { NotesPopover } from './NotesPopover';
import { SourceTags } from './SourceTags';

export interface BidAskDetails {
  avg: number;
  min: number | null;
  max: number | null;
}

export interface KeyTermsRowProps<T extends KeyTermType>
  extends BaseKeyTermLogicProps<T> {
  isExpanded: boolean;
  setExpandedRow: (row: T | null) => void;
  label: string;
  source?: MarketDataSourceType;
  confidence?: ConfidenceLevel;
  distanceMiles?: number;
  timePeriodMonths?: number;
  errorMessage?: string;
  notes?: string | null;
  buyersAgentUser?: Partial<Pick<UserObject, 'fullName' | 'profilePicture'>>;
  onUpdateValue: (value?: number | null) => void;
  onUpdateNotes: (notes?: string | null) => void;
  isViewOnly?: boolean;
  isAutofillingKeyTerms?: boolean;
}

export const KeyTermsRow = memo(function KeyTermsRow<T extends KeyTermType>(
  props: KeyTermsRowProps<T>
) {
  const {
    isExpanded,
    setExpandedRow,
    label,
    range,
    row,
    unit,
    source,
    confidence,
    distanceMiles,
    timePeriodMonths,
    errorMessage,
    value,
    notes,
    onUpdateValue,
    onUpdateNotes,
    buyersAgentUser,
    isViewOnly,
    isAutofillingKeyTerms,
  } = props;

  const [inputValue, setInputValue] = useState<number | null | undefined>(
    value
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isSingleRangeValue,
    getRangeTrackTooltipContent,
    getAnchor,
    getMainValueLabel,
    getDotColorValue,
    getHighlightedRange,
    getPlaceholderValue,
    getRangeLabel,
  } = useKeyTermOverrideLogic({
    ...props,
    row,
  });

  const showRangeChart = !!(!isSingleRangeValue && range && unit);

  const isNilValue = isNil(value);

  const sourceLabel = source?.toLowerCase()?.split('_')?.join(' ');

  const mainValueLabel = getMainValueLabel?.();
  const placeholder = getPlaceholderValue();
  const rangeLabel = getRangeLabel();
  const dotColor = getDotColorValue(inputValue);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Flex
      ref={containerRef}
      borderBottom="1px solid"
      borderBottomColor="whiteAlpha.200"
      direction="column"
      justifyContent="center"
      px={4}
      onBlur={() => {
        if (!isViewOnly) {
          setExpandedRow(null);
        }
      }}
      onClick={() => {
        if (inputRef.current !== document.activeElement) {
          inputRef.current?.focus();
        }
      }}
      onFocus={() => {
        if (!isSingleRangeValue && range && !isViewOnly) {
          setExpandedRow(row);
        }
      }}
      onMouseDown={() => {
        if (
          inputRef.current !== document.activeElement &&
          !isExpanded &&
          !isViewOnly
        ) {
          inputRef.current?.focus();
        }
      }}
    >
      <Flex
        alignItems="center"
        gap={6}
        height={isExpanded ? EXPANDED_ROW_HEIGHT : COLLAPSED_ROW_HEIGHT}
        transition="height 0.3s ease-in-out"
      >
        <Flex flexBasis={0} flexGrow={1} height="100%" py={4}>
          <Flex direction="column" height="100%" justifyContent="space-between">
            <Text fontWeight="medium" textTransform="capitalize">
              {label}
            </Text>
          </Flex>
        </Flex>
        <Flex flexBasis={0} flexGrow={4} height="100%">
          <Flex
            direction="column"
            height="100%"
            justifyContent="space-between"
            py={4}
            width="100%"
          >
            <Flex gap={4} height="100%">
              <Flex flexBasis={0} flexGrow={2} height="100%">
                <Flex direction="column" gap={1}>
                  {mainValueLabel}
                  {(!!rangeLabel || !!mainValueLabel) && (
                    <>
                      <Text
                        color={mainValueLabel ? 'whiteAlpha.600' : 'white'}
                        fontSize={mainValueLabel ? '16px' : '24px'}
                        fontWeight={mainValueLabel ? 'normal' : 'medium'}
                        lineHeight="1"
                      >
                        {rangeLabel}
                      </Text>
                      <SourceTags
                        confidence={confidence as unknown as SignalStrength}
                        distanceMiles={distanceMiles}
                        isExpanded={isExpanded}
                        sourceLabel={sourceLabel}
                        timePeriodMonths={timePeriodMonths}
                      />
                    </>
                  )}
                </Flex>
                {errorMessage &&
                  (!isViewOnly ? (
                    <Flex alignItems="center" gap={1}>
                      <Icon as={CircleAlertIcon} color="red.500" />
                      <Text>{errorMessage}</Text>
                    </Flex>
                  ) : (
                    <Text
                      color="whiteAlpha.600"
                      fontSize="24px"
                      fontWeight="medium"
                      mt={2}
                    >
                      N/A
                    </Text>
                  ))}
              </Flex>
              <Flex
                flexBasis={0}
                flexGrow={2}
                gap={4}
                justifyContent={isViewOnly ? 'space-between' : 'center'}
              >
                {isViewOnly ? (
                  showRangeChart && !isNilValue ? (
                    <Flex alignItems="flex-end" mt={6} width="100%">
                      <KeyTermsRangeChart
                        anchor={getAnchor?.()}
                        dotColor={dotColor}
                        fullRange={range}
                        getRangeTrackTooltipContent={
                          getRangeTrackTooltipContent
                        }
                        highlightedRange={getHighlightedRange?.()}
                        showValue={true}
                        source={source}
                        unit={unit}
                        value={value}
                      />
                    </Flex>
                  ) : (
                    <Text
                      color={isNilValue ? 'whiteAlpha.600' : 'white'}
                      fontSize="24px"
                      fontWeight="medium"
                      mt={2}
                    >
                      {isNilValue
                        ? 'To be discussed'
                        : formatAgentRecommendation({
                            value,
                            unit,
                            shortForm: false,
                          })}
                    </Text>
                  )
                ) : (
                  <Flex
                    justifyContent="center"
                    position="relative"
                    width="100%"
                  >
                    {unit === KeyTermUnit.USD && (
                      <CurrencyInput
                        ref={inputRef}
                        color={isAutofillingKeyTerms ? 'cyan.500' : 'white'}
                        data-testid={`${row}-input`}
                        fontSize="24px"
                        fontWeight="medium"
                        placeholder={
                          placeholder ? formatToCurrency(placeholder) : '$0'
                        }
                        transition="color 0.3s ease-in-out"
                        value={inputValue ?? undefined}
                        onBlur={() => {
                          onUpdateValue(inputValue);
                        }}
                        onValueChange={(value) => {
                          const newParsedValue = value
                            ? Number.parseFloat(value)
                            : null;
                          setInputValue(newParsedValue);
                        }}
                      />
                    )}
                    {unit === KeyTermUnit.DAYS && (
                      <NumberInput
                        color={isAutofillingKeyTerms ? 'cyan.500' : 'white'}
                        size="lg"
                        transition="color 0.3s ease-in-out"
                        value={inputValue ?? ''}
                        width="100%"
                        onBlur={() => {
                          onUpdateValue(inputValue);
                        }}
                        onChange={(_, value) => {
                          if (isNaN(value)) {
                            setInputValue(null);
                          } else {
                            setInputValue(value);
                          }
                        }}
                      >
                        <NumberInputField
                          ref={inputRef}
                          backgroundColor="input.bg"
                          data-testid={`${row}-input`}
                          fontSize="24px"
                          fontWeight="medium"
                          placeholder={
                            placeholder ? `${placeholder} days` : '0 days'
                          }
                          value={value ?? ''}
                        />
                      </NumberInput>
                    )}
                    {showRangeChart && !isNilValue && (
                      <Flex
                        backgroundColor={dotColor}
                        borderRadius="full"
                        boxSize={2}
                        position="absolute"
                        right={3}
                        top={5}
                      />
                    )}
                  </Flex>
                )}
                <Flex minWidth={8} paddingTop={2}>
                  <NotesPopover
                    isViewOnly={isViewOnly}
                    label={label}
                    noteUser={buyersAgentUser}
                    notes={notes}
                    onChange={(notes) => onUpdateNotes(notes)}
                  />
                </Flex>
              </Flex>
            </Flex>
            {isExpanded && showRangeChart && (
              <KeyTermsRangeChart
                anchor={getAnchor?.()}
                dotColor={dotColor}
                fullRange={range}
                getRangeTrackTooltipContent={getRangeTrackTooltipContent}
                highlightedRange={getHighlightedRange?.()}
                source={source}
                unit={unit}
                value={inputValue}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});
